import { Link, useLocation } from "react-router-dom";
import {
  CONTACT_DETAILS,
  CUSTOMER_NORMAL_FOOTER_ITEMS,
  LOCAL_STORAGE_KEYS,
  MENULIST_SECTION_USER_ACCOUNT_OPTIONS,
  ROLES,
  SOCIAL_MEDIA,
} from "../../constants";
import SvgIcon from "../../assets/icons/SvgIcon";
import Button from "../Button";
import "../../assets/scss/customer-layout.scss";
import { useEffect, useState } from "react";
import { createSubscribeMail } from "../../services/customer/home";
import Loader from "../Loader";
import { ENVIRONMENT } from "../../clientConfig";
import moment from "moment";
import { getLocalStorage } from "../../utils";
import CustomerAuth from "../Modal/Customer/Auth/CustomerAuthLayout";
import "./footer.scss";
import Input from "../Input";
import { USER_ROUTES } from "../../constants/appRoutes";

const CustomerFooter = () => {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };
  const isAuthenticated = getLocalStorage(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
  const userRole = getLocalStorage(LOCAL_STORAGE_KEYS.USER_ROLE);
  const sellerType = getLocalStorage(LOCAL_STORAGE_KEYS.SELLER_TYPE);
  const currentUrl = useLocation();

  const handleLoading = (data: boolean) => {
    setLoading(data);
  };

  const handleOnSubmit = () => {
    setError("");

    if (!email.trim()) {
      setError("Email is required.");
      return;
    }

    if (!validateEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    handleLoading(true);
    createSubscribeMail({ email }, handleLoading);
  };

  useEffect(() => {
    setError("");
    setEmail("");
  }, [currentUrl.pathname]);

  const accessWithAuthenticationPostProperty = (): boolean => {
    const propertyAccess = MENULIST_SECTION_USER_ACCOUNT_OPTIONS.filter(
      (item) => item.url === USER_ROUTES.YOUR_PROPERTY
    );

    const checkAccess = propertyAccess.some((option) => {
      const hasAccess = option?.access?.includes(userRole);
      const isSellerWithAccess =
        userRole === ROLES.SELLER ? option?.seller?.includes(sellerType) : true;

      return hasAccess && isSellerWithAccess;
    });

    return checkAccess;
  };

  return (
    <footer className="border-t border-t-gray-200 footer-section">
      <div className="sm:px-10 py-4 divide-y divide-dark-cyan">
        <div className="gap-6 grid grid-cols-12 px-8 pt-10 pb-20 information-section">
          <div className="col-span-12 xl:col-span-6 section-first">
            <div className="flex justify-center md:justify-start items-center">
              <SvgIcon
                iconName="savitar-realty-logo"
                className="w-72 h-auto max-h-16"
              />
            </div>
            <div className="mt-8">
              <h3 className="font-bold text-dark-cyan text-xl">Subscribe</h3>
              <p className="font-normal text-base">
                Subscribe to stay tuned for new property and latest updates.
                Let's do it!
              </p>
              <div className="flex sm:flex-row flex-col justify-start items-center gap-4 mt-4 w-full xl:max-w-2xl">
                <Input
                  type="email"
                  name="subscriber_email"
                  placeholder="Enter your email address"
                  className="!mb-0 subscribe-input-box"
                  id="subscriber_email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={error}
                />
                <div className="w-full sm:w-fit">
                  <Button
                    type="submit"
                    className="mt-[2px] sm:mt-0 py-6 rounded-md w-full h-0"
                    disabled={loading}
                    onClick={handleOnSubmit}
                  >
                    {loading ? <Loader /> : "Subscribe"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
          <div className="space-y-6 col-span-12 md:col-span-6 xl:col-span-3 section-second">
            <div>
              <h3 className="font-bold text-dark-cyan text-2xl">Contact Us</h3>
              <div className="mt-4">
                <p className="font-normal text-black text-lg">
                  {CONTACT_DETAILS.toll_free}
                </p>
                <p className="font-normal text-black text-lg">
                  9:30 AM to 8:30 PM (Mon- Sun)
                </p>
                <p className="font-normal text-black text-lg">
                  {CONTACT_DETAILS.phone_number}
                </p>
                <p className="font-normal text-black text-lg">
                  {CONTACT_DETAILS.email}
                </p>
              </div>
            </div>
            <div>
              <h3 className="font-bold text-dark-cyan text-2xl">Follow Us</h3>
              <ul className="flex justify-start items-center mt-4">
                {SOCIAL_MEDIA.map((social, index) => (
                  <li key={index} className="px-4 py-3 social-media">
                    <Link
                      to={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <SvgIcon
                        iconName={social?.iconName}
                        className={`size-6 ${social?.className ?? ""}`}
                      />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="col-span-12 md:col-span-6 xl:col-span-3 section-third">
            <div>
              {ENVIRONMENT === "local" && (
                <h3 className="font-bold text-dark-cyan text-2xl">
                  Download the App
                </h3>
              )}
              <div className="space-y-3 mt-4">
                {ENVIRONMENT === "local" && (
                  <div className="flex sm:flex-row flex-col justify-start items-start sm:items-center gap-3 w-full">
                    <Button type="button" className="py-5 rounded-md h-0">
                      <SvgIcon iconName="appStoreIcon" />
                    </Button>
                    <Button type="button" className="py-5 rounded-md h-0">
                      <SvgIcon iconName="playStoreIcon" />
                    </Button>
                  </div>
                )}
                <p className="font-normal text-gray-400 text-sm">
                  Usage of savitarrealty.com to upload content showing area in
                  non standard units or which enables targeting by
                  religion/community/caste/race is prohibited. Please report
                  inappropriate content by writing to us at report abuse
                </p>
                <p className="font-medium text-black text-xl">
                  All trademarks and rights are the property of their respective
                  owners.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-center items-center py-4">
          <ul className="flex flex-wrap justify-center gap-4">
            {CUSTOMER_NORMAL_FOOTER_ITEMS.filter(
              (item) =>
                item.label !== "Post your property" ||
                accessWithAuthenticationPostProperty()
            ).map(({ label, url }) => (
              <li key={label} className="px-2 sm:px-4 py-2">
                {label === "Post your property" && !isAuthenticated ? (
                  <CustomerAuth btnName={label} btnClassName="login-btn" />
                ) : (
                  <Link
                    to={url}
                    className="font-medium text-black hover:text-dark-cyan text-sm sm:text-base"
                  >
                    {label}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="bg-dark-cyan py-4">
        <p className="font-semibold text-white text-base text-center">
          Savitar Realty | © {moment().year()} All right reserved
        </p>
      </div>
    </footer>
  );
};

export default CustomerFooter;
